import type { Claim, ExpenseSummary } from '../../../../types/LegalCase';
import { formatCurrency } from '../../../../utils/collateralFormatters';

// Resultado esperado do caso: confronta o que ainda dá para recuperar (pretensão) com o que o
// caso já custou (despesas). NÃO soma custo na pretensão — são naturezas distintas (o que se
// cobra do devedor vs. o que a Agrolend gastou); apenas confronta. Versão enxuta (Task 11): o
// recuperável não desconta depósitos levantados — esses aparecem à parte na seção Depósitos.
export interface CaseResult {
    // Nenhum dos dois lados existe (sem pretensão e sem custo): não há o que confrontar.
    hasData: boolean;
    hasClaim: boolean;
    hasCost: boolean;
    recoverable: number;
    recoverableFormatted: string;
    cost: number;
    costFormatted: string;
    // net = recoverable - cost. Só faz sentido quando há pretensão (senão é só "-custo").
    net: number;
    netFormatted: string;
    // Resultado só é significativo quando há pretensão para confrontar.
    hasNet: boolean;
    // Custo como % do recuperável (custo/recuperável*100). Responde "vale a pena?": 15% ok,
    // 90% acende alerta. Só faz sentido com recuperável > 0. NÃO é capado em 100 — custo acima
    // do recuperável (>100%) é justamente o sinal que interessa.
    costRatioPct: number;
    costRatioFormatted: string;
    hasCostRatio: boolean;
}

// "Recuperável" = o **valor atualizado** da dívida (`charges.updatedAmount`): saldo em aberto +
// juros e multa calculados pelas taxas do caso + custas. É o que responde "quanto ele me deve
// hoje" — a pergunta do card.
//
// Por que não o `remainingTotal`: ele é a pretensão **como registrada** menos o pago, e os juros
// das taxas do caso NÃO entram ali (só entram se alguém digitar o valor em reais). Um caso com
// taxa cadastrada mostrava "saldo a recuperar" sem os juros que estão correndo — achado na
// validação da AF-3816. Os dois números continuam existindo (ADR 004): o registrado vai na
// petição, o atualizado responde quanto se tem a receber.
//
// Fallback para `remainingTotal` quando não há `charges` (sem taxa cadastrada, ou resposta anterior
// à Task 17): melhor o número de antes que zero.
const recoverableFromClaim = (claim: Claim | null): number => {
    if (!claim) return 0;
    const updated = claim.charges?.updatedAmount;
    if (updated != null) return updated;
    return claim.remainingTotal || 0;
};

export const computeCaseResult = (
    claim: Claim | null,
    expensesSummary: ExpenseSummary | null | undefined,
): CaseResult => {
    const hasClaim = !!claim && (claim.grandTotal > 0 || claim.remainingTotal > 0);
    const cost = expensesSummary?.total || 0;
    const hasCost = cost > 0;
    const recoverable = recoverableFromClaim(claim);
    const net = recoverable - cost;

    // % do custo sobre o recuperável — só depende de haver saldo (denominador). Custo zero dá
    // 0% (métrica válida, não escondida): um caso com saldo e sem despesa mostra "0%". Sem
    // saldo a recuperar não há denominador, aí a % não faz sentido e é omitida.
    const hasCostRatio = recoverable > 0;
    const costRatioPct = hasCostRatio ? (cost / recoverable) * 100 : 0;

    return {
        hasData: hasClaim || hasCost,
        hasClaim,
        hasCost,
        recoverable,
        recoverableFormatted: formatCurrency(recoverable),
        cost,
        costFormatted: formatCurrency(cost),
        net,
        netFormatted: formatCurrency(net),
        hasNet: hasClaim,
        costRatioPct,
        costRatioFormatted: `${costRatioPct.toLocaleString('pt-BR', { maximumFractionDigits: 1 })}%`,
        hasCostRatio,
    };
};
